import { Environment } from "../helpers/environment";

export interface Settings {
  environment: Environment;
  apiDomain: string;
  wwwDomain: string;
  // Domain for static source files (JS, CSS, etc)
  staticDomain: string;
  hashFiles: boolean;
  // Domain for images
  assetDomain: string;
  forceSSL: boolean;

  // external service tokens/ids
  stripePublishableKey: string;
  stripeApiVersion: string;
  intercomAppId: string;
  sentryServerDSN: string;
  sentryClientDSN: string;
  sentryRelease: string;
  prerenderToken: string;
  googleTagManagerUrl: string;
  affiliateTracking: boolean;
  apolloAppId?: string;
  linkedInPartnerId?: string;
  hubspotId?: string;
  clarityId?: string;
}

const base: Settings = {
  environment: Environment.development,
  apiDomain: "",
  wwwDomain: "",
  staticDomain: undefined,
  hashFiles: true,
  assetDomain: "",
  forceSSL: true,
  prerenderToken: "3iBK4RKxo8YahwyORNjq",
  // Sentry release is added to the ECS environment
  sentryRelease: process.env["SENTRY_RELEASE"],
  sentryServerDSN: "https://9641d6735c934ba597520e4b065107b1@o24487.ingest.sentry.io/5748005",
  sentryClientDSN: "https://a4729b507a934e30baa9b6194cadcc4b@o24487.ingest.sentry.io/225513",
  stripePublishableKey: "pk_test_H5L5jNLDmJq4OLLwWNMB0OVs",
  stripeApiVersion: "2016-07-06",
  intercomAppId: "cmmlfxfe",
  googleTagManagerUrl: null,
  hubspotId: null,
  affiliateTracking: false,
};

const envSettings: Record<Environment, Partial<Settings>> = {
  [Environment.testRunner]: {
    environment: Environment.testRunner,
    apiDomain: "http://api:9000",
    wwwDomain: "http://local.lingoapp.com",
    staticDomain: "",
    hashFiles: false,
    assetDomain: "",
    forceSSL: false,
    // Disable monitoring for development
    sentryClientDSN: null,
    sentryServerDSN: null,
    googleTagManagerUrl: null,
  },
  [Environment.development]: {
    environment: Environment.development,
    apiDomain: "http://localhost:9000",
    wwwDomain: "http://local.lingoapp.com:8080",
    staticDomain: "",
    assetDomain: "/assets",
    hashFiles: false,
    forceSSL: false,
    // Disable monitoring for development
    sentryClientDSN: null,
    sentryServerDSN: null,
    googleTagManagerUrl:
      "https://www.googletagmanager.com/gtm.js?id=GTM-5ZHDFX2&gtm_auth=-ulgjXCkBHDus651qLwrSw&gtm_preview=env-13&gtm_cookies_win=x",
  },
  [Environment.test]: {
    environment: Environment.test,
    apiDomain: "https://api-test.lingoapp.com",
    wwwDomain: "https://www-test.lingoapp.com",
    staticDomain: "https://static-test.lingoapp.com",
    assetDomain: "https://static-test.lingoapp.com/assets",
    hashFiles: true,
    googleTagManagerUrl:
      "https://www.googletagmanager.com/gtm.js?id=GTM-5ZHDFX2&gtm_auth=-ulgjXCkBHDus651qLwrSw&gtm_preview=env-13&gtm_cookies_win=x",
  },
  [Environment.staging]: {
    environment: Environment.staging,
    apiDomain: "https://api-staging.lingoapp.com",
    wwwDomain: "https://www-staging.lingoapp.com",
    staticDomain: "https://static-staging.lingoapp.com",
    assetDomain: "https://static-staging.lingoapp.com/assets",
    hashFiles: true,
    googleTagManagerUrl:
      "https://www.googletagmanager.com/gtm.js?id=GTM-5ZHDFX2&gtm_auth=q3ka_nSJzGIsgb5drvjQzw&gtm_preview=env-14&gtm_cookies_win=x",
  },
  [Environment.production]: {
    environment: Environment.production,
    apiDomain: "https://api.lingoapp.com",
    wwwDomain: "https://www.lingoapp.com",
    staticDomain: "https://static.lingoapp.com",
    assetDomain: "https://static.lingoapp.com/assets",
    hashFiles: true,
    stripePublishableKey: "pk_live_C87DEEBwKppeFaAqRJ07LsrM",
    intercomAppId: "nxbdfliw",
    sentryServerDSN: null,
    affiliateTracking: true,
    googleTagManagerUrl:
      "https://www.googletagmanager.com/gtm.js?id=GTM-5ZHDFX2&gtm_auth=854UsnuX4fr-Z8ZiezdC8Q&gtm_preview=env-1&gtm_cookies_win=x",
    apolloAppId: "67d073c1c640e20015b092eb",
    linkedInPartnerId: "7100028",
    hubspotId: "243178612",
    clarityId: "rkkjizu8qn",
  },
};

function getEnv(): Environment {
  if (typeof window !== "undefined" && window?._lingoEnvironment) {
    return window?._lingoEnvironment as Environment;
  } else if (global?._lingoEnvironment) {
    return global._lingoEnvironment as Environment;
  } else if (process.env.ENVIRONMENT) {
    return process.env.ENVIRONMENT as Environment;
  } else if (process.env.NODE_ENV === "development") {
    return Environment.development;
  }
}

const currentEnv = getEnv();
const overrides = envSettings[currentEnv];

if (!overrides) {
  const options = Object.keys(envSettings).join(", ");
  throw Error(`Unexpected environment "${currentEnv}". expected one of [ ${options} ]`);
}

export default { ...base, ...overrides } as Settings;
